import { formatMoney } from "@babackapp/ui/lib/format-money";
import { cn } from "@babackapp/ui/lib/utils";
import { Column, Hr, Img, Row, Section, Text } from "@react-email/components";
import React from "react";
import type { PackageContentBlockProps } from "./types";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { useEmailThemeRadius } from "../../hooks";


type Translation = {
  key: string;
  value?: string | null;
  locale: string;
};

type LineItemProps = {
  name: string;
  imageUrl: string;
  sku: string;
  quantity: number;
  price: number;
  priceCurrency?: string | null;
  textColor: string;
  translations?: Translation[];
  customerLocale?: string;
  hidePrices?: boolean;
};


function getTranslatedName(
  defaultName: string,
  translations?: Translation[],
  locale?: string,
): string {
  if (!translations || !locale) return defaultName;

  const translation = translations.find(
    (t) => {
      const tLocale = t.locale?.split("-")[0];
      return t.key === "title" && tLocale === locale && t.value
    },
  );

  return translation?.value || defaultName;
}

function LineItem({
  name,
  imageUrl,
  quantity,
  price,
  priceCurrency,
  textColor,
  translations,
  customerLocale,
  hidePrices,
}: LineItemProps) {
  const imageRadius = "rounded-lg";
  const displayName = getTranslatedName(name, translations, customerLocale);

  return (
    <Row className="">
      <Column className="w-[64px] p-1">
        <Img
          src={imageUrl}
          width={64}
          height={64}
          alt={displayName}
          className={cn("w-[64px] h-[64px]", imageRadius, "object-cover")}
        />
      </Column>
      <Column className="pl-2">
        <Text
          className="m-0 p-0 leading-[1.4] font-semibold"
          style={{ color: textColor }}
        >
          {displayName} x {quantity}
        </Text>
        {!hidePrices && (
          <Text
            className="m-0 p-0 leading-[1.4] opacity-60"
            style={{ color: textColor }}
          >
            {priceCurrency
              ? formatMoney(price, priceCurrency, customerLocale)
              : `${price}€`}
          </Text>
        )}
      </Column>
    </Row>
  );
}

function OrderRecapContainer({
  children,
  contrastColor,
  theme,
}: React.PropsWithChildren & {
  contrastColor: string;
  theme: PackageContentBlockProps["theme"];
}) {
  const radius = useEmailThemeRadius(theme, "packageContentTable");

  return (
    <Section className={radius}>
      <Row
        className={cn(radius, "border-1 border-solid")}
        style={{ borderWidth: 1, borderColor: contrastColor }}
      >
        <Column>{children}</Column>
      </Row>
    </Section>
  );
}

export function PackageContentEmailRenderer({
  block,
  parcel,
  theme,
  section,
  locale,
}: PackageContentBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));

  if (!parcel?.lineItems) {
    return null;
  }

  return (
    <Section>
      <OrderRecapContainer theme={theme} contrastColor={colors.contrast}>
        {parcel.lineItems?.map((item, index) => {
          const itemWithMetadata = item as typeof item & {
            metadata?: { translations?: Translation[] };
          };
          const translations = itemWithMetadata.metadata?.translations;

          return (
            <React.Fragment key={item.sku}>
              <LineItem
                name={item.name}
                imageUrl={item.imageUrl}
                sku={item.sku}
                quantity={item.quantity}
                price={item.price}
                priceCurrency={item.priceCurrency}
                textColor={colors.text}
                translations={translations}
                customerLocale={locale}
                hidePrices={block.properties?.hidePrices}
              />
              {index < parcel.lineItems.length - 1 && (
                <Hr
                  className="border"
                  style={{ borderColor: colors.contrast }}
                />
              )}
            </React.Fragment>
          );
        })}
      </OrderRecapContainer>
    </Section>
  );
}
